import { isDefined } from './validation.helper';

/**
 * @returns new array with swapped values
 */
export function swapValues<T>(_array: T[], index1: number, index2: number): T[] {
  const value1 = _array[index1];
  _array[index1] = _array[index2];
  _array[index2] = value1;
  return [..._array];
}

/**
 * Removes the element at index from _array
 * @returns a new array object
 */
export function removeByIndex<T>(_array: T[], index: number): T[] {
  const newArray = [..._array];
  newArray.splice(index, 1);
  return newArray;
}

export function updateByIndex<T>(_array: T[], index: number, newValue: T | ((value: T) => T)): T[] {
  const newArray = [..._array];
  if (typeof newValue === 'function') {
    const existingValue = _array[index];
    newArray[index] = (newValue as (value: T) => T)(existingValue);
  } else {
    newArray[index] = newValue;
  }

  return newArray;
}

export function updateByPredicate<T>(
  _array: T[],
  predicateFunc: (value: T) => boolean,
  newValue: T | ((value: T) => T),
  throwErroWhenNotFound = true
): T[] {
  const index = _array.findIndex((item) => predicateFunc(item));

  if (index === -1) {
    if (throwErroWhenNotFound) {
      throw new Error('update operation error - item not found');
    }
  } else {
    return updateByIndex(_array, index, newValue);
  }
  return _array;
}

/**
 * Removes elements that match the predicate function
 * @return a new array with elements removed
 */
export function removeByPredicate<T>(_array: T[], predicateFunc: (value: T) => boolean): T[] {
  return _array.filter((item) => !predicateFunc(item));
}

/**
 * Removes elements with a certain value from an array
 *
 */
export function removeByValue<T>(_array: T[], value: T): T[] {
  return removeByPredicate(_array, (currentValue: T) => currentValue === value);
}

/**
 * Removes the first element found for value parameter
 * @returns a new array object withouth the item found by value
 */
export function removeFirstByValue<T>(_array: T[], value: T): T[] {
  const index = _array.findIndex((currentValue) => currentValue === value);
  if (isDefined(index) && index !== -1) {
    return removeByIndex(_array, index);
  }
  return _array;
}

export function removeLastElements<T>(_array: T[], numberOfElemements: number) {
  if (_array.length < numberOfElemements) {
    return _array;
  }
  const newArray = [..._array];
  newArray.splice(newArray.length - numberOfElemements, numberOfElemements);

  return newArray;
}

export function takeFirstElements<T>(_array: T[], numberOfElemements: number) {
  if (_array.length < numberOfElemements) {
    return _array;
  }
  return _array.slice(0, numberOfElemements);
}

/**
 * Base Component which manages redux store with implementing lifecycle methods
 * @deprecated use ES7 arr.includes() instead
 */
export function containsValue(_array: any[], value: any): boolean {
  for (const currentValue of _array) {
    if (currentValue === value) {
      return true;
    }
  }
  return false;
}

export function sortAscending(value1: any, value2: any): number {
  if (value1 < value2) {
    return -1;
  } else if (value1 > value2) {
    return 1;
  } else {
    return 0;
  }
}

export function unique(array: any[], compareKey: string): any[] {
  return (
    array
      .map((e) => e[compareKey])
      // store the keys of the unique objects
      .map((e, i, final) => final.indexOf(e) === i && i)
      // eliminate the dead keys & store unique objects
      .filter((e) => array[e])
      .map((e) => array[e])
  );
}

/**
 * Erzeugt aus einem Array ein Object. Das Property des Object wird durch den Property Name des Array definiert
 *
 * z.B.
 * const items = [{id: 1, name: 'Hugo'}, {id: 2, name: 'Mizzi'}];
 *
 * wenn propertyName "id" -> Resultat ist {1: {id: 1, name: 'Hugo'}, ...}
 *
 */
export function createObjectFromArray<T, R>(
  _array: T[],
  propertyNameResolver: (string | number) | ((T) => string | number),
  valueResolver?: (T) => R
): Record<string | number, T | R> {
  return _array.reduce((obj: { [index: string | number]: any }, item: any) => {
    const realyPropertyName =
      typeof propertyNameResolver === 'string' || typeof propertyNameResolver === 'number'
        ? item[propertyNameResolver]
        : propertyNameResolver(item);
    obj[realyPropertyName] = valueResolver ? valueResolver(item) : item;
    return obj;
  }, {});
}

/**
 * Erzeugt aus einem Array ein Object Array. Das Property des Object wird durch den Property Name des Array definiert
 *
 * z.B.
 * const items = [{type: 'a', name: 'Hugo'}, {type: 'b', name: 'Mizzi'}, {type: 'b', name: 'Franzi'}];
 *
 * wenn propertyNameResolver "id" -> Resultat ist {'a': [{type: 'a', name: 'Hugo'}], {'b': [{type: 'b', name: 'Mizzi'}, {type: 'b', name: 'Franzi'}]}
 *
 */
export function createObjectArrayFromArray<T extends object, R>(
  _array: T[],
  propertyNameResolver: string | ((T) => string),
  valueResolver?: (T) => R
): Record<string, (T | R)[]> {
  return _array.reduce((obj: { [index: string]: any }, item: T) => {
    const keyValue = typeof propertyNameResolver === 'string' ? item[propertyNameResolver] : propertyNameResolver(item);

    if (!obj[keyValue]) {
      obj[keyValue] = [];
    }

    const resultArray: (T | R)[] = obj[keyValue];
    resultArray.push(valueResolver ? valueResolver(item) : item);
    return obj;
  }, {});
}

/**
 * Sortiert array und liefert ein "neues" Array zurück
 *
 * @param _array - zu sortierendes Array von Objekten
 * @param valueProvider - Funktion die den zu sortierenden Wert liefert
 * @param order - 'asc' oder 'dsc'
 */
export function sortArray<T extends object>(
  _array: T[],
  valueProvider: (T) => string | number,
  order: 'asc' | 'dsc' = 'asc'
): T[] {
  const orderMultiplikator = order === 'asc' ? 1 : -1;
  const result = [..._array].sort((t1: T, t2: T) => {
    const val1 = valueProvider(t1);
    const val2 = valueProvider(t2);
    if (val1 < val2) {
      return -1 * orderMultiplikator;
    }
    if (val1 > val2) {
      return 1 * orderMultiplikator;
    }
    return 0;
  });
  return result;
}

/**
 * Teilt ein Array in ein Array von Arrays mit einer Grösse die in "chunkSize" angegeben wird
 */
export function chunkArray<T>(_array: T[], chunkSize: number): T[][] {
  return Array.from({ length: Math.ceil(_array.length / chunkSize) }, (v, i) =>
    _array.slice(i * chunkSize, i * chunkSize + chunkSize)
  );
}

export function sumArray(_array: number[]): number {
  return _array.reduce((a, b) => a + b, 0);
}

export function sumArrayObject<T extends object>(
  _array: T[],
  valueProvider: (item: T, index?: number) => number
): number {
  return _array.reduce((a, b, currentIndex: number) => a + valueProvider(b, currentIndex), 0);
}

/**
 *  Ermittelt aus einem Array von Objekten "eindeutige" Einträge. Duplikate werden entfernt...
 *
 *  z.B.
 *
 *   const items = [{id: 1, value: 'Value 1'}, {id: 1, value: 'Value 1'},{id: 2, value: 'Value 2'}, {id: 1, value: 'Value 1'}   ];
 *
 *   const result = getDistinctObjects(items, item => item.id);
 *
 *   -> [{id: 1, value: 'Value 1'}, {id: 2, value: 'Value 2'}]
 *
 */

export function getDistinctObjects<T extends object>(
  _array: T[],
  valueResolver: (string | number) | ((T) => unknown)
): T[] {
  const uniqueIds = new Set<unknown>();
  return _array.filter((obj) => {
    const value =
      typeof valueResolver === 'string' || typeof valueResolver === 'number' ? obj[valueResolver] : valueResolver(obj);

    if (uniqueIds.has(value)) {
      return false; // Duplikate nicht hinzufügen
    }
    uniqueIds.add(value);
    return true; // nur noch nicht vorhandene objects hinzufügen
  });
}

export function arrayFromDefinedItems<T>(...items: T[]) {
  return items.filter((item) => !!item);
}

export function addOrRemoveStringArrayHandling(arr: string[], str: string): string[] {
  const index = arr.indexOf(str);
  if (index !== -1) {
    // wenn der string vorhanden ist dann entfernen
    return [...arr.slice(0, index), ...arr.slice(index + 1)];
  } else {
    // wenn der string nicht vorhanden ist dann hinzufügen
    return [...arr, str];
  }
}
